import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

export type ApiKey = components['schemas']['GetApiKeysEndpoint_Response'];

export interface UseApiKeysReturn extends BaseQuery {
  apiKeys: ApiKey[];
}

export function useApiKeys(organizationId: number, enabled: boolean = true): UseApiKeysReturn {
  const { getAccessToken } = useAuth();

  const getApiKeys = async (organizationId: number) => {
    const client = createApiClient(getAccessToken);
    const { data } = await client.GET(`/api/organizations/{organizationId}/api-keys`, {
      params: {
        path: { organizationId },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data;
  };

  const { data, isLoading, isPending, error, refetch, isRefetching } = useQuery({
    queryKey: ['apiKeys', organizationId],
    queryFn: () => getApiKeys(organizationId),
    enabled: enabled && organizationId > 0,
    retry: false,
  });

  return {
    apiKeys: data ?? [],
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
